import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import StatCard from '../../components/admin/StatCard';
import {
  Users, UserCheck, FileText, Clock, Briefcase, ShieldCheck,
  AlertCircle, Star, BarChart3, Mail, MessageSquare, Send
} from 'lucide-react';
import { format } from 'date-fns';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await adminAPI.getOverviewStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-semibold text-navy-900">
          Admin Dashboard
        </h1>
        <p className="text-navy-500 mt-1">
          Welcome back, {user?.first_name}. Here's your platform overview.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats?.totalUsers || 0} color="primary" />
        <StatCard icon={UserCheck} label="Active Users" value={stats?.activeUsers || 0} color="green" />
        <StatCard icon={FileText} label="Total Requests" value={stats?.totalRequests || 0} color="navy" />
        <StatCard icon={Clock} label="Active Requests" value={stats?.activeRequests || 0} color="gold" />
        <StatCard icon={Briefcase} label="Total Helpers" value={stats?.totalHelpers || 0} color="purple" />
        <StatCard icon={ShieldCheck} label="Verified Helpers" value={stats?.verifiedHelpers || 0} color="green" />
        <StatCard
          icon={AlertCircle}
          label="Pending Verifications"
          value={stats?.pendingVerifications || 0}
          color="gold"
          onClick={() => navigate('/admin/helpers')}
        />
        <StatCard icon={Star} label="Avg Rating" value={stats?.averageRating || '0'} color="gold" />
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => navigate('/admin/analytics')} className="btn-primary flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> View Analytics
          </button>
          <button onClick={() => navigate('/admin/helpers')} className="btn-secondary flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Verify Helpers
          </button>
          <button onClick={() => navigate('/admin/messaging')} className="btn-secondary flex items-center gap-2">
            <Mail className="h-4 w-4" /> Send Notification
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-semibold text-navy-900">Recent Users</h2>
            <button onClick={() => navigate('/admin/users')} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View all
            </button>
          </div>
          <div className="space-y-3">
            {stats?.recentUsers?.map(u => (
              <div key={u.id} className="flex items-center justify-between py-2 border-b border-navy-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-navy-200 rounded-full flex items-center justify-center">
                    <span className="text-navy-600 text-xs font-medium">
                      {u.first_name?.[0]}{u.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-navy-900">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-navy-500">{u.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    u.user_type === 'helper' ? 'bg-purple-100 text-purple-700' :
                    u.user_type === 'admin' ? 'bg-red-100 text-red-700' :
                    'bg-primary-100 text-primary-700'
                  }`}>
                    {u.user_type?.replace('_', ' ')}
                  </span>
                  <p className="text-xs text-navy-400 mt-1">
                    {u.created_at ? format(new Date(u.created_at), 'MMM d') : ''}
                  </p>
                </div>
              </div>
            ))}
            {(!stats?.recentUsers || stats.recentUsers.length === 0) && (
              <p className="text-sm text-navy-400 text-center py-4">No users yet</p>
            )}
          </div>
        </div>

        {/* Recent Requests */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-semibold text-navy-900">Recent Requests</h2>
            <button onClick={() => navigate('/admin/requests')} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View all
            </button>
          </div>
          <div className="space-y-3">
            {stats?.recentRequests?.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-navy-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-navy-900">{r.port_name || 'Unknown port'}</p>
                  <p className="text-xs text-navy-500">by {r.owner_first_name} {r.owner_last_name}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={r.status} />
                  <p className="text-xs text-navy-400 mt-1">
                    {r.scheduled_date || ''}
                  </p>
                </div>
              </div>
            ))}
            {(!stats?.recentRequests || stats.recentRequests.length === 0) && (
              <p className="text-sm text-navy-400 text-center py-4">No requests yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Platform Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Send} label="Completed" value={stats?.completedRequests || 0} color="green" />
        <StatCard icon={MessageSquare} label="Conversations" value={stats?.totalConversations || 0} color="primary" />
        <StatCard icon={Star} label="Total Ratings" value={stats?.totalRatings || 0} color="gold" />
        <StatCard icon={AlertCircle} label="Cancelled" value={stats?.cancelledRequests || 0} color="red" />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-blue-100 text-blue-800',
    matched: 'bg-indigo-100 text-indigo-800',
    confirmed: 'bg-primary-100 text-primary-800',
    in_progress: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-navy-100 text-navy-700'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}
